/**
 * Ray Related Look
 *
 * "Рекомендовані доповнення для образу" row (blocks/ray-related-look.liquid). Lets the shopper
 * pick each item's color/size inline and add it to the cart with a single "+" click, no
 * navigation. Mirrors the per-item variant-resolution pattern already proven in
 * assets/ray-bundle-offer.js, minus the bundling/totals logic this block doesn't need.
 */
(() => {
  if (window.RayRelatedLookInitialized) return;

  window.RayRelatedLookInitialized = true;

  const CART_UPDATE_EVENT = 'cart:update';

  const selectors = {
    root: '[data-ray-related-look]',
    item: '[data-ray-related-look-item]',
    variantsJson: '[data-ray-related-look-item-variants]',
    variantIdInput: '[data-ray-related-look-variant-id]',
    optionControl: '[data-ray-related-look-option]',
    sizeSelect: '[data-ray-related-look-size-select]',
    price: '[data-ray-related-look-item-price]',
    comparePrice: '[data-ray-related-look-item-compare-price]',
    addButton: '[data-ray-related-look-add]',
    error: '[data-ray-related-look-error]',
  };

  const getVariants = (item) => {
    const json = item.querySelector(selectors.variantsJson);

    if (!json) return [];

    try {
      return JSON.parse(json.textContent.trim());
    } catch (error) {
      console.error('Ray related look variants JSON parse failed', error);
      return [];
    }
  };

  const applyMoneyFormat = (cents, format) => {
    const amount = (cents / 100).toFixed(2);

    if (!format) return amount;

    const noDecimal = Math.round(cents / 100).toString();
    const withComma = amount.replace('.', ',');

    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', noDecimal)
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimal);
  };

  const getMoneyFormatter = (root) => {
    const format = root.dataset.moneyFormat;

    return (amountInCents) => applyMoneyFormat(Number(amountInCents) || 0, format);
  };

  const getSelectedOptions = (item) => {
    const selectedOptions = {};

    item.querySelectorAll(selectors.optionControl).forEach((control) => {
      const position = control.dataset.optionPosition;

      if (!position) return;
      if (control.type === 'radio' && !control.checked) return;

      selectedOptions[`option${position}`] = control.value;
    });

    return selectedOptions;
  };

  const variantMatchesOptions = (variant, selectedOptions) => {
    return Object.entries(selectedOptions).every(([key, value]) => !value || variant[key] === value);
  };

  const findVariant = (item) => {
    const variants = getVariants(item);
    const selectedOptions = getSelectedOptions(item);

    if (Object.keys(selectedOptions).length === 0) {
      return variants.find((variant) => variant.available) || variants[0];
    }

    return (
      variants.find((variant) => variant.available && variantMatchesOptions(variant, selectedOptions)) ||
      variants.find((variant) => variantMatchesOptions(variant, selectedOptions)) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  };

  const updateSizeAvailability = (item) => {
    const select = item.querySelector(selectors.sizeSelect);

    if (!select) return;

    const variants = getVariants(item);
    const selectedOptions = getSelectedOptions(item);
    const position = select.dataset.optionPosition;
    const key = `option${position}`;
    const optionsWithoutSize = { ...selectedOptions };

    delete optionsWithoutSize[key];

    Array.from(select.options).forEach((option) => {
      const testOptions = { ...optionsWithoutSize, [key]: option.value };
      const hasAvailableVariant = variants.some((variant) => variant.available && variantMatchesOptions(variant, testOptions));

      option.disabled = !hasAvailableVariant;
    });

    if (select.selectedOptions[0]?.disabled) {
      const firstAvailable = Array.from(select.options).find((option) => !option.disabled);

      if (firstAvailable) select.value = firstAvailable.value;
    }
  };

  const updateItem = (item, formatMoney) => {
    updateSizeAvailability(item);

    const variant = findVariant(item);
    const variantIdInput = item.querySelector(selectors.variantIdInput);
    const priceEl = item.querySelector(selectors.price);
    const comparePriceEl = item.querySelector(selectors.comparePrice);
    const addButton = item.querySelector(selectors.addButton);
    const unavailableLabel = item.closest(selectors.root)?.dataset.unavailableLabel || '';

    if (!variant) return;

    if (variantIdInput) variantIdInput.value = variant.id;

    if (priceEl) {
      priceEl.textContent = variant.available ? formatMoney(variant.price) : unavailableLabel;
    }

    if (comparePriceEl) {
      if (variant.available && variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compareAtPrice);
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.hidden = true;
      }
    }

    if (addButton) addButton.disabled = !variant.available;
  };

  const getItemVariant = (item) => {
    const variants = getVariants(item);
    const variantIdInput = item.querySelector(selectors.variantIdInput);

    if (!variantIdInput) return null;

    return variants.find((variant) => String(variant.id) === String(variantIdInput.value)) || null;
  };

  const showError = (root, message) => {
    const errorEl = root.querySelector(selectors.error);

    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = !message;
  };

  const addItemToCart = async (item, root) => {
    const variant = getItemVariant(item);

    if (!variant || !variant.available) return;

    const addButton = item.querySelector(selectors.addButton);

    showError(root, '');

    if (addButton) {
      addButton.disabled = true;
      addButton.dataset.loading = 'true';
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      if (response.status) {
        showError(root, response.message || response.description || 'Cart error');
        return;
      }

      const cart = await fetch('/cart.js').then((res) => res.json());

      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: root.id || 'ray-related-look',
            data: {
              source: 'ray-related-look',
              itemCount: 1,
              sections: response.sections,
            },
          },
        })
      );
    } catch (error) {
      console.error('Ray related look add-to-cart failed', error);
      showError(root, 'Cart error');
    } finally {
      if (addButton && addButton.isConnected) {
        delete addButton.dataset.loading;

        const currentVariant = getItemVariant(item);

        addButton.disabled = !currentVariant || !currentVariant.available;
      }
    }
  };

  let instanceCounter = 0;

  const initRoot = (root) => {
    if (root.dataset.rayRelatedLookInitialized === 'true') return;

    root.dataset.rayRelatedLookInitialized = 'true';

    if (!root.id) root.id = `ray-related-look-${++instanceCounter}`;

    const formatMoney = getMoneyFormatter(root);
    const items = Array.from(root.querySelectorAll(selectors.item));

    items.forEach((item) => updateItem(item, formatMoney));

    root.addEventListener('change', (event) => {
      if (!event.target.closest(selectors.optionControl)) return;

      const item = event.target.closest(selectors.item);

      if (item) updateItem(item, formatMoney);
    });

    root.addEventListener('click', (event) => {
      const addButton = event.target.closest(selectors.addButton);

      if (!addButton) return;

      const item = addButton.closest(selectors.item);

      if (item) addItemToCart(item, root);
    });
  };

  const initAll = (rootNode = document) => {
    rootNode.querySelectorAll(selectors.root).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', () => initAll());
})();
